import React from "react";
import { GripVertical, Pencil, Plus } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragNumberInput from "../ui/DragNumberInput";
import InfoTooltip from "../ui/InfoTooltip";
import MenuSelect from "../ui/MenuSelect";
import PanelDeleteIconButton from "../draggable-panels/PanelDeleteIconButton";
import Tooltip from "../ui/Tooltip";
import { TENDON_SPATIAL_ROUTE_GRID_CLASS } from "../../constants/ui";
import { stripNamespacePrefix } from "../../utils/nameUtils";
import PropertyPanelSection from "./PropertyPanelSection";
import {
  SPATIAL_ROUTE_INFO,
  SPATIAL_ROUTE_PREVIEW_LIMIT,
  SPATIAL_ROUTE_TYPE_OPTIONS,
} from "./tendonPanelConstants";

function SortableSpatialRouteEntry({ id, index, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { index },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group ${TENDON_SPATIAL_ROUTE_GRID_CLASS} items-start px-1 py-1 text-xs`}
    >
      <div className="flex items-center gap-1 text-gray-400">
        <span className="w-4 text-right">{index + 1}</span>
        <Tooltip content="Drag to reorder">
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded-sm panel-text-helper hover:bg-gray-800 hover:text-gray-200 cursor-grab active:cursor-grabbing"
            aria-label="Drag route element"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
      </div>
      {children}
    </div>
  );
}

const SpatialTendonRouteSection = ({
  tendon,
  spatialRoute,
  showFullRouteEditor,
  siteOptions,
  geomOptions,
  onAddRouteEntry,
  onUpdateRouteEntry,
  onChangeRouteKind,
  onRemoveRouteEntry,
  onRouteDragEnd,
  onOpenFullRouteEditor,
}) => (
  <PropertyPanelSection
    title={
      <span className="flex min-w-0 items-center gap-1 truncate text-left">
        <span className="truncate">Spatial Route</span>
        <InfoTooltip text={SPATIAL_ROUTE_INFO} />
      </span>
    }
    actions={
      showFullRouteEditor ? (
        <button
          type="button"
          onClick={() => onAddRouteEntry("site")}
          className="flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-gray-600 hover:border-gray-400 text-gray-300 hover:text-gray-100"
        >
          <Plus className="w-3 h-3" />
          Add
        </button>
      ) : (
        <Tooltip content="Edit">
          <button
            type="button"
            onClick={onOpenFullRouteEditor}
            className="flex h-6 w-6 items-center justify-center rounded border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-gray-100"
            aria-label="Edit spatial route"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </Tooltip>
      )
    }
    actionsClassName="gap-1"
  >
    {!showFullRouteEditor ? (
      <div className="space-y-2">
        <div className="space-y-1">
          {spatialRoute.slice(0, SPATIAL_ROUTE_PREVIEW_LIMIT).map((entry, index) => {
            const routeKind = entry?.kind || "site";
            const ref =
              routeKind === "geom"
                ? entry?.geom
                : routeKind === "pulley"
                  ? `divisor ${entry?.divisor ?? 1}`
                  : entry?.site;
            return (
              <div
                key={`${tendon.uuid}-route-preview-${index}`}
                className="grid grid-cols-[2rem_5rem_minmax(0,1fr)] items-center gap-2 px-1 py-0.5 text-xs"
              >
                <div className="text-right panel-text-helper">{index + 1}</div>
                <div className="text-gray-400">{routeKind}</div>
                <div className="min-w-0 truncate text-gray-200">
                  {stripNamespacePrefix(ref) || "-"}
                </div>
              </div>
            );
          })}
          {spatialRoute.length > SPATIAL_ROUTE_PREVIEW_LIMIT ? (
            <div className="px-1 text-xs panel-text-helper">
              +{spatialRoute.length - SPATIAL_ROUTE_PREVIEW_LIMIT} more
            </div>
          ) : null}
        </div>
      </div>
    ) : (
      <>
        <div
          className={`${TENDON_SPATIAL_ROUTE_GRID_CLASS} px-1 text-xs uppercase tracking-wider panel-text-helper`}
        >
          <div>#</div>
          <div>Type</div>
          <div>Reference</div>
          <div />
        </div>
        <div className="space-y-1">
          {spatialRoute.length === 0 ? (
            <div className="panel-text-helper text-xs border border-gray-700 rounded p-2">
              No route elements
            </div>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={onRouteDragEnd}>
              <SortableContext
                items={spatialRoute.map((_, index) => `${tendon.uuid}-route-${index}`)}
                strategy={verticalListSortingStrategy}
              >
                {spatialRoute.map((entry, index) => {
                  const routeKind = entry?.kind || "site";
                  const routeId = `${tendon.uuid}-route-${index}`;
                  return (
                    <SortableSpatialRouteEntry key={routeId} id={routeId} index={index}>
                      <MenuSelect
                        size="compact"
                        value={routeKind}
                        onChange={(value) => onChangeRouteKind(index, value)}
                        options={SPATIAL_ROUTE_TYPE_OPTIONS}
                        triggerClassName="min-w-0"
                        contentClassName="max-h-[min(220px,50vh)]"
                        aria-label="Spatial route element type"
                      />
                      {routeKind === "geom" ? (
                        <div className="col-start-2 col-span-2 row-start-2 grid min-w-0 grid-cols-1 gap-2 pt-1">
                          <MenuSelect
                            size="compact"
                            value={entry?.geom || ""}
                            onChange={(value) => onUpdateRouteEntry(index, { geom: value })}
                            options={geomOptions}
                            placeholder="Geom"
                            triggerClassName="min-w-0"
                            contentClassName="max-h-[min(240px,50vh)]"
                            aria-label="Spatial route geom"
                          />
                          <MenuSelect
                            size="compact"
                            value={entry?.sidesite || ""}
                            onChange={(value) =>
                              onUpdateRouteEntry(index, { sidesite: value || undefined })
                            }
                            options={[{ value: "", label: "No sidesite" }, ...siteOptions]}
                            placeholder="No sidesite"
                            triggerClassName="min-w-0"
                            contentClassName="max-h-[min(240px,50vh)]"
                            aria-label="Spatial route sidesite"
                          />
                        </div>
                      ) : routeKind === "pulley" ? (
                        <DragNumberInput
                          label="Divisor"
                          value={Number(entry?.divisor ?? 1)}
                          dragValue={Number(entry?.divisor ?? 1)}
                          onChange={(event) =>
                            onUpdateRouteEntry(index, {
                              divisor: Number(event.target.value) || 0,
                            })
                          }
                          onDragValueChange={(value) =>
                            onUpdateRouteEntry(index, { divisor: Number(value) || 0 })
                          }
                          wrapperClassName="space-y-0"
                          labelClassName="sr-only"
                          inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-xs"
                          inputProps={{ step: "0.01", "aria-label": "Pulley divisor" }}
                        />
                      ) : (
                        <MenuSelect
                          size="compact"
                          value={entry?.site || ""}
                          onChange={(value) => onUpdateRouteEntry(index, { site: value })}
                          options={siteOptions}
                          triggerClassName="min-w-0"
                          contentClassName="max-h-[min(240px,50vh)]"
                          aria-label="Spatial route site"
                        />
                      )}
                      <PanelDeleteIconButton
                        onClick={() => onRemoveRouteEntry(index)}
                        ariaLabel="Remove route element"
                        title="Remove route element"
                        className="col-start-4 row-start-1 self-start"
                        revealOnParentHover
                      />
                    </SortableSpatialRouteEntry>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </>
    )}
  </PropertyPanelSection>
);

export default SpatialTendonRouteSection;
